import { useApolloClient } from '@apollo/client';
import { Paper } from '@mui/material';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Cell, LabelList, Legend, Pie, PieChart } from 'recharts';
import * as Yup from 'yup';
import { DiamondIcon } from '../../assets/DiamondIcon.tsx';
import { JackCrown } from '../../assets/JackCrown.tsx';
import { JokerCrown } from '../../assets/JokerCrown.tsx';
import { KingCrown } from '../../assets/KingCrown.tsx';
import { QueenCrown } from '../../assets/QueenCrown.tsx';
import { UserSmilingBetter } from '../../assets/UserSmilingBetter.tsx';
import { Button } from '../../components/Button/Button.tsx';
import { Footer } from '../../components/Footer/Footer.tsx';
import { Header } from '../../components/Header/Header.tsx';
import { ScoreDocument } from '../../components/Header/header.gen.ts';
import Hero from '../../components/Hero/Hero.tsx';
import { ExamsSummaryOrderBy, ExamsSummaryOrderDirection } from '../../graphql/types.gen.ts';
import { getRankDetailsByPoints } from '../../helpers.ts';
import { useIsMobile } from '../../helpers/useIsMobile.ts';
import { setToken } from '../../helpers/useValidateToken.ts';
import { GENERAL_GROUP_NAME } from '../../index.tsx';
import {
	LastGroupDocument,
	MeDocument,
	useLastGroupLazyQuery,
	useMeQuery,
} from '../FormPage/LoginPage/loginPage.gen.ts';
import {
	BackofficeStatisticsDocument,
	BackofficeStatisticsLeaderboardsDocument,
	BackofficeStatisticsPublicDocument,
	BackofficeStatisticsPublicLeaderboardsDocument,
	GetJoiningRequestestsDocument,
} from '../Statistics/statistics.gen.ts';
import { useStatistics } from '../Statistics/useStatistics.ts';
import './Profil.scss';
import { useProfilStatistics } from './hooks/useProfilStatistics.ts';
import {
	useAccountDeleteMutation,
	useGroupJoinRequestCreateMutation,
	useGroupSwitchSaveMutation,
	usePasswordChangeMutation,
} from './profil.gen.ts';

export const passwordChangeSchema = Yup.object().shape({
	currentPassword: Yup.string().required('Hasło jest wymagane').min(8, 'Hasło jest za krótkie'),
	newPassword: Yup.string().required('Hasło jest wymagane').min(8, 'Hasło jest za krótkie'),
});

const COLORS = ['#880518', '#2b8723', '#1b4c16'];

export const joinGroupSchema = Yup.object().shape({
	groupCode: Yup.string().required('Nazwa grupy jest wymagana'),
});

const GROUP_MEMEBER_ROLE_MAP = {
	INSTRUCTOR: 'Trener',
	PUPIL: 'Uczeń',
};
export const Profil = () => {
	const client = useApolloClient();
	const navigate = useNavigate();
	const { data, refetch: refetchMe } = useMeQuery({
		variables: {
			input: {
				ordering: {
					orderBy: ExamsSummaryOrderBy.Tag,
					orderDirection: ExamsSummaryOrderDirection.Desc,
				},
			},
		},
	});
	const [refetchLastGroup, { data: chosenGroupData }] = useLastGroupLazyQuery({ fetchPolicy: 'no-cache' });

	useEffect(() => {
		refetchLastGroup();
		refetchMe();
	}, []);

	const chosenGroup = chosenGroupData?.me?.recentlyUsedGroupId || GENERAL_GROUP_NAME;
	const { statisticsData, statisticsPublicData, leaderboardsActivityData, leaderboardsRatioData } =
		useProfilStatistics();
	const { isMobile } = useIsMobile();
	const [isPasswordInputShown, setIsPasswordInputShown] = useState(false);
	const statisticsSource = statisticsData?.group || statisticsPublicData?.publicGroups[0];
	const myStatistics = statisticsSource?.me;
	const myScore = statisticsSource?.me?.score;
	const myExamResults = myStatistics?.examsSummary;
	const { userPassedExamsRatio, transformedTagDataWithIds } = useStatistics(myExamResults);
	const [groupJoinRequestCreateMutation, { data: groupJoinData }] = useGroupJoinRequestCreateMutation();
	const totalExams = userPassedExamsRatio.reduce((acc, item) => acc + item.value, 0);
	const passedExams = userPassedExamsRatio.find((item) => item.id === 'passed');
	const rankDetails = getRankDetailsByPoints(myScore);
	const rankRatio = (myScore * 100) / (rankDetails.toNextRank + myScore);
	const [passwordChangeMutation, { data: passwordChangeMutationData }] = usePasswordChangeMutation();

	const [accountDeleteMutation, { data: accountDeleteMutationData }] = useAccountDeleteMutation();
	const [groupSwitchSaveMutation] = useGroupSwitchSaveMutation({
		refetchQueries: [
			LastGroupDocument,
			ScoreDocument,
			MeDocument,
			ScoreDocument,
			BackofficeStatisticsDocument,
			BackofficeStatisticsLeaderboardsDocument,
			BackofficeStatisticsPublicLeaderboardsDocument,
			BackofficeStatisticsPublicDocument,
			GetJoiningRequestestsDocument,
		],
	});

	useEffect(() => {
		if (!groupJoinData?.groupJoinRequestCreate) return;
		if (groupJoinData?.groupJoinRequestCreate?.__typename === 'CreateJoinRequestSuccessView') {
			toast.success('Zgłoszenie dołączenia do grupy zostało wysłane');
		} else {
			toast.error(handleJoinRequestError(groupJoinData?.groupJoinRequestCreate?.failureReason));
		}
	}, [groupJoinData]);

	useEffect(() => {
		if (passwordChangeMutationData?.passwordChange.success === true) {
			navigate('/login');
		}
		if (passwordChangeMutationData?.passwordChange && !passwordChangeMutationData.passwordChange.success) {
			toast.error('Ooops, wystąpił błąd');
		}
	}, [passwordChangeMutationData]);

	useEffect(() => {
		if (accountDeleteMutationData?.accountDelete.success) {
			toast.success('Twoje konto zostało usunięte pomyślnie');
		}
		if (accountDeleteMutationData?.accountDelete && !accountDeleteMutationData.accountDelete.success) {
			toast.error('Ooops, wystąpił błąd');
		}
	}, [accountDeleteMutationData]);

	const rankDetailsToIcon = (r: string) => {
		switch (r) {
			case 'jack-crown':
				return <JackCrown />;
			case 'queen-crown':
				return <QueenCrown />;
			case 'king-crown':
				return <KingCrown />;
			case 'joker-crown':
			default:
				return <JokerCrown />;
		}
	};
	const handleJoinRequestError = (reason: string) => {
		switch (reason) {
			case 'GROUP_NOT_FOUND':
				return 'Nie znaleziono grupy o podanym kodzie';
			case 'ALREADY_MEMBER':
				return 'Już jesteś członkiem tej grupy';
			case 'FOUND_PENDING_REQUEST':
				return 'Już wysłałeś prośbę o dołączenie do tej grupy';
			case 'CANNOT_JOIN_PUBLIC_GROUP':
				return 'Nie udało się wysłać prośby o dołączenie do grupy';
			default:
				return 'Nie udało się dołączyć do grupy';
		}
	};
	const handleGroupSwitchError = (reason: string) => {
		switch (reason) {
			case 'GROUP_NOT_FOUND':
				return 'Nie znaleziono grupy o podanej nazwie';
			case 'NOT_A_MEMBER':
				return 'Nie jesteś członkiem tej grupy';
			default:
				return 'Nie udało się dołączyć do grupy';
		}
	};
	return (
		<div className="profile light statistics">
			<div className="content">
				<Header light={true} />
				<Hero title="Profil" />
				<div className="profile__container container">
					<Paper className="profile__paper  profile__paper--center">
						<h2 className="profile__paperTitle">Twój profil:</h2>
						<div className="profile__profileInfo">
							<div className="profile__avatar">
								<UserSmilingBetter />
							</div>
							<div className="profile__paperCol">
								<p className="profile__username">{data?.me.username}</p>
								<p className="profile__email">{data?.me.email}</p>
								{!isPasswordInputShown && (
									<Button
										variant="text"
										onClick={() => {
											setIsPasswordInputShown(true);
										}}
									>
										Zmień hasło
									</Button>
								)}
							</div>
						</div>
						{isPasswordInputShown && (
							<Formik
								initialValues={{
									currentPassword: '',
									newPassword: '',
								}}
								validationSchema={passwordChangeSchema}
								onSubmit={(values) => {
									passwordChangeMutation({
										variables: {
											input: values,
										},
									});
								}}
							>
								{(formik) => {
									const { errors, touched, isValid, dirty } = formik;
									return (
										<>
											<Form>
												<div className="formPage__formGroup form-row">
													<Field
														type="password"
														placeholder="Obecne hasło*"
														name="currentPassword"
														id="currentPassword"
														className={`formPage__formControl ${errors.currentPassword && touched.currentPassword ? 'input-error' : ''}`}
													/>
													<ErrorMessage
														name="currentPassword"
														component="span"
														className="error"
													/>
												</div>
												<div className="formPage__formGroup form-row">
													<Field
														type="password"
														placeholder="Nowe hasło*"
														name="newPassword"
														id="newPassword"
														className={`formPage__formControl ${errors.newPassword && touched.newPassword ? 'input-error' : ''}`}
													/>
													<ErrorMessage
														name="newPassword"
														component="span"
														className="error"
													/>
												</div>
												<button
													type="submit"
													className={`button formPage__button${!(dirty && isValid) ? ' disabled' : ''}`}
													disabled={!(dirty && isValid)}
												>
													Zmień hasło
												</button>
											</Form>
										</>
									);
								}}
							</Formik>
						)}
					</Paper>
					<div className="row">
						<Paper className="profile__paper profile__paper--between profile__paper--40">
							<h2 className="profile__paperTitle">Twoje punkty</h2>
							<p className="profile__score">
								{myScore || 0}
								<DiamondIcon />
							</p>
							<p className="noMb">
								Kaźdy dobrze wykonany test dodaje Ci punkty!
								<br />
								Ale kaźdy źle wykonany je odejmuje...
							</p>
						</Paper>
						<Paper className="profile__paper profile__paper--center profile__paper--60">
							<h2 className="profile__paperTitle">Twoja ranga</h2>
							<div className="profile__rankWrapper">
								<div className="row justify--between">
									<div className="profile__col">
										<div className="profile__imgWrapper">{rankDetailsToIcon(rankDetails.img)}</div>
									</div>
									<div className="profile__col">
										<div className="row justify--between">
											<p className="profile__rankDetails">{rankDetails.text}</p>
											<p className="profile__rankDetailsScore">
												{myScore || 0}/{rankDetails.toNextRank + myScore || 0} <DiamondIcon />
											</p>
										</div>
										<div className="profile__progress">
											<div
												className="profile__progress--fill"
												style={{ width: `${rankRatio}%` }}
											></div>
										</div>
										<p className="profile__progress__desc">
											Zdobądź {rankDetails.toNextRank + myScore || 0} <DiamondIcon /> by przejść
											do kolejnej rangi
										</p>
									</div>
								</div>
							</div>
						</Paper>
					</div>
					<Paper className="profile__paper profile__paper--center">
						<h2 className="profile__paperTitle">Twoje grupy:</h2>
						<div className="profile__groups">
							{data?.me.groups.map((group) => {
								const shortenedName =
									group.name.length >= 18 ? group.name.slice(0, 15) + '...' : group.name;
								const d = new Date(group.me.memberSince);
								const datestring = d.toISOString().slice(0, 10);
								const rankDetailsPerGroup = getRankDetailsByPoints(group.me.score);
								return (
									<div
										key={group.code}
										className={`profile__group ${chosenGroup === group.code ? 'profile__group--logged' : ''}`}
									>
										<p className="profile__group__name">
											{group.name === GENERAL_GROUP_NAME
												? 'Wspólny język - grupa publiczna Licytapki'
												: group.name}
										</p>
										{chosenGroup === group.code && (
											<p className="profile__group__logged">Obecnie zalogowany</p>
										)}
										{group.me.role === 'INSTRUCTOR' && (
											<button
												onClick={() => {
													navigator.clipboard.writeText(group?.code);
													toast.success('Skopiowano kod grupy');
												}}
												className="profile__group__copy"
											>
												Skopiuj kod grupy: {group?.code}
											</button>
										)}
										<div className="profile__group__content">
											<div className="profile__group__col profile__group__col--rank">
												{rankDetailsToIcon(rankDetailsPerGroup.img)}
												<p className="profile__group__rank">{rankDetailsPerGroup.text}</p>
											</div>
											<div className="profile__group__col">
												<p className="profile__group__role">
													Rola: {GROUP_MEMEBER_ROLE_MAP[group.me.role]}
												</p>
												<p className="profile__group__date">Od: {datestring}</p>
												<p className="profile__group__score">
													{group.me.score} <DiamondIcon />
												</p>
											</div>
										</div>
										<div className="profile__group__buttons">
											{chosenGroup === group.code ? (
												<>
													<Button variant="secondary" onClick={() => navigate('/nauka')}>
														Nauka
													</Button>
													<Button variant="secondary" onClick={() => navigate('/licytacja')}>
														Testy
													</Button>
												</>
											) : (
												<Button
													variant="secondary"
													onClick={async () => {
														const res = await groupSwitchSaveMutation({
															variables: { input: { groupCode: group.code } },
														});
														if (
															res.data?.groupSwitchSave?.__typename ===
															'GroupSwitchSaveSuccessView'
														) {
															await refetchLastGroup();
															toast.success('Zmieniono grupę');
															navigate('/profil');
														} else {
															toast.error(
																handleGroupSwitchError(
																	res.data?.groupSwitchSave?.reason
																)
															);
														}
													}}
												>
													Zaloguj się do {shortenedName}
												</Button>
											)}
										</div>
									</div>
								);
							})}

							<Formik
								initialValues={{
									groupCode: '',
								}}
								validationSchema={joinGroupSchema}
								onSubmit={(v) => {
									groupJoinRequestCreateMutation({
										variables: { input: { groupCode: v.groupCode } },
									});
								}}
								enableReinitialize={true}
							>
								{(formik) => {
									const { errors, touched, isValid, dirty } = formik;

									return (
										<div className="profile__group">
											<p className="profile__group__name">Dołącz do grupy</p>
											<p className="profile__group__logged">
												Jeżeli otrzymałeś kod grupy od trenera, wpisz go poniżej
											</p>
											<Form>
												<div className="formPage__formGroup form-row">
													<Field
														type="groupCode"
														placeholder="Kod grupy*"
														name="groupCode"
														id="groupCode"
														className={`formPage__formControl ${errors.groupCode && touched.groupCode ? 'input-error' : ''}`}
													/>
													<ErrorMessage name="groupCode" component="span" className="error" />
												</div>
												<button
													type="submit"
													className={`button formPage__button${!(dirty && isValid) ? ' disabled' : ''}`}
													disabled={!(dirty && isValid)}
												>
													Dołącz do grupy
												</button>
											</Form>
										</div>
									);
								}}
							</Formik>
						</div>
					</Paper>
					<div className="row">
						{!!leaderboardsActivityData?.length && !!leaderboardsRatioData?.length && (
							<Paper className="profile__paper profile__paper--evenly profile__paper--40">
								<h2 className="profile__paperTitle">Leaderzy grupy</h2>
								<div className="profile__leaderboard">
									<h3 className="profile__leaderboard__title">Najwięcej zrobionych testów:</h3>
									<ol className="profile__leaderboard__list">
										{leaderboardsActivityData &&
											leaderboardsActivityData.map((member) => {
												const totalExams = member.totalExamsNumber;
												const username = member.user.username;
												return (
													<li key={username} className="profile__leaderboard__item">
														{username}: {totalExams}
													</li>
												);
											})}
									</ol>
								</div>
								<div className="profile__leaderboard">
									<h3 className="profile__leaderboard__title">
										Najlepsi użytkownicy: <DiamondIcon />
									</h3>
									<ol className="profile__leaderboard__list">
										{leaderboardsRatioData &&
											leaderboardsRatioData.map((member) => {
												const score = member.score;
												const username = member.user.username;
												return (
													<li key={username} className="profile__leaderboard__item">
														{username}: {score}
													</li>
												);
											})}
									</ol>
								</div>
							</Paper>
						)}
						{!!userPassedExamsRatio[0]?.ratioNumber && (
							<Paper className="profile__paper  profile__paper--center profile__paper--60">
								<h2 className="profile__paperTitle">Twój postęp</h2>
								<ul className="profile__ul">
									<li className="profile__li">
										<strong className="profile__columnItemTitle">Wszystkie wykonane testy: </strong>
										<span className="profile__columnItemValue">{totalExams}</span>
									</li>
									<li className="profile__li">
										<strong className="profile__columnItemTitle">Poprawnie wykonane testy: </strong>
										<span className="profile__columnItemValue">
											{passedExams.ratio}% ({passedExams.value})
										</span>
									</li>
								</ul>
								<PieChart width={isMobile ? 300 : 400} height={isMobile ? 300 : 400}>
									<Legend iconType="circle" />
									<Pie
										data={userPassedExamsRatio}
										dataKey="value"
										nameKey="name"
										cx="50%"
										cy="50%"
										outerRadius={isMobile ? 100 : 150}
									>
										{userPassedExamsRatio.map((entry, index) => (
											<React.Fragment key={index}>
												<Cell fill={COLORS[index % COLORS.length]} />
												<LabelList fill="#fff" />
											</React.Fragment>
										))}
									</Pie>
								</PieChart>
							</Paper>
						)}
					</div>
					<Paper className="profile__paper profile__paper--center profile__paper--bigStatistics">
						<h2 className="profile__paperTitle">Poszczególne umiejętności</h2>

						<div className="table">
							<div className="row row-header">
								<div className="cell">Umiejętność</div>
								<div className="cell">Wszystkie</div>
								<div className="cell">Zaliczone</div>
								<div className="cell">Procent </div>
							</div>
							{transformedTagDataWithIds?.map((tag) => {
								const percent = (100 * tag.passed) / tag.total || 0;

								return (
									<div className="row" key={tag?.tag}>
										<div className="cell">{tag.tag}</div>
										<div className="cell">{tag.total}</div>
										<div className="cell">{tag.passed}</div>
										<div className={`cell ${percent < 40 && tag.total !== 0 ? 'red' : ''}`}>
											{percent.toFixed()}%
										</div>
									</div>
								);
							})}
						</div>
					</Paper>
					<Button
						variant="text"
						onClick={async () => {
							client.cache.reset();
							setToken('');
							navigate('/');
						}}
					>
						Wyloguj
					</Button>
					<Button
						variant="textRed"
						onClick={async () => {
							await accountDeleteMutation({
								variables: {
									input: {
										feedback: '',
									},
								},
							}).then(async () => {
								client.cache.reset();
								setToken('');
								navigate('/login');
							});
						}}
					>
						Usuń konto
					</Button>
				</div>
			</div>
			<Footer />
		</div>
	);
};
